import { VariableGroup, VariableComponent, CssVariable } from '../types';
import { groupBy } from 'lodash';
import { variablesMetaData, getBasicVariablesMetaData, allSDKEnabledVars } from '../data';
import { CustomCssVariables } from '@thoughtspot/visual-embed-sdk';

/**
 * Defines the order in which variable groups should be displayed for each component type.
 * This ensures consistent UI layout across different embedding contexts.
 */
const sortOrders: Partial<Record<VariableComponent, VariableGroup[]>> = {
  [VariableComponent.Default]: [
    VariableGroup.Application,
    VariableGroup.Liveboard,
    VariableGroup.Charts,
    VariableGroup.Button,
    VariableGroup.Chip,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Checkbox,
    VariableGroup.Advanced,
    VariableGroup.Search,
    VariableGroup.Spotter,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.SpotIQ,
  ],
  [VariableComponent.App]: [
    VariableGroup.Application,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.Checkbox,
    VariableGroup.Button,
    VariableGroup.Advanced,
    VariableGroup.Charts,
    VariableGroup.Liveboard,
    VariableGroup.Chip,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Search,
    VariableGroup.Spotter,
    VariableGroup.SpotIQ,
  ],
  [VariableComponent.Liveboard]: [
    VariableGroup.Application,
    VariableGroup.Liveboard,
    VariableGroup.Charts,
    VariableGroup.Button,
    VariableGroup.Chip,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Checkbox,
    VariableGroup.Advanced,
    VariableGroup.Search,
    VariableGroup.Spotter,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.SpotIQ,
  ],
  [VariableComponent.Spotter]: [
    VariableGroup.Application,
    VariableGroup.Spotter,
    VariableGroup.Charts,
    VariableGroup.Button,
    VariableGroup.Chip,
    VariableGroup.Search,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Checkbox,
    VariableGroup.Advanced,
    VariableGroup.Liveboard,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.SpotIQ,
  ],
  [VariableComponent.Search]: [
    VariableGroup.Application,
    VariableGroup.Search,
    VariableGroup.Charts,
    VariableGroup.Button,
    VariableGroup.Chip,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Checkbox,
    VariableGroup.Advanced,
    VariableGroup.Liveboard,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.Spotter,
    VariableGroup.SpotIQ,
  ],
  [VariableComponent.Viz]: [
    VariableGroup.Application,
    VariableGroup.Charts,
    VariableGroup.Button,
    VariableGroup.Chip,
    VariableGroup.Menu,
    VariableGroup.Dialog,
    VariableGroup.Checkbox,
    VariableGroup.Advanced,
    VariableGroup.Search,
    VariableGroup.Liveboard,
    VariableGroup.Spotter,
    VariableGroup.Navbar,
    VariableGroup.Home,
    VariableGroup.ModularHomepage,
    VariableGroup.List,
    VariableGroup.SpotIQ,
  ],
};

/**
 * Gets the sort order for a specific component type.
 * Falls back to the default sort order if the component type is not defined.
 *
 * @param component - The component type to get sort order for
 * @returns Array of variable groups in the correct order
 */
const getSortOrder = (component: VariableComponent) => {
  return sortOrders[component] || sortOrders.Default;
};

/**
 * Helper function that processes a list of variables into a grouped and sorted format.
 * This is the core logic shared between basic and full variable processing.
 *
 * @param functionalVariables - Array of variables to process
 * @param component - Component type to determine sort order
 * @returns Array of grouped variables with proper ordering and sub-grouping
 */
const processVariablesIntoGroups = (
  functionalVariables: CustomCssVariables[],
  component: VariableComponent
) => {
  // Get the sort order for the specified component
  const sortOrder = sortOrders[component] || sortOrders[VariableComponent.Default] || [];

  // Group variables by their main group (e.g., 'Application', 'Button', etc.)
  const groupedVariablesMap = groupBy(functionalVariables, (variable) => variable.group);

  // Create ordered groups based on the sort order
  const orderedGroups: Array<{ name: string; groupedVariable: CssVariable[] }> = [];

  sortOrder.forEach((groupName) => {
    if (groupedVariablesMap[groupName]) {
      orderedGroups.push({
        name: groupName,
        groupedVariable: groupedVariablesMap[groupName] as CssVariable[],
      });
    }
  });

  // Further group variables within each main group by their sub-group
  const subGroupedVariables = orderedGroups.map((group) => {
    const subGroupedMap = groupBy(group.groupedVariable, (variable) => variable.subGroup || '');
    const subGroupedVariables = Object.entries(subGroupedMap).map(([key, value]) => {
      return {
        name: key,
        variables: value as CssVariable[],
      };
    });
    return { name: group.name, subGroup: subGroupedVariables };
  });

  return subGroupedVariables;
};

/**
 * Gets basic variables (without feature flags) grouped and sorted for immediate display.
 * This function is synchronous and doesn't wait for authentication or feature flag data.
 * Used to show variables immediately while full variables load in the background.
 *
 * @param releaseVariables - Object containing available variable keys
 * @param component - Component type to determine sort order
 * @returns Array of grouped variables ready for immediate display
 */
export const getBasicGroupedVariables = (
  releaseVariables: any,
  component: VariableComponent = VariableComponent.Default
) => {
  const releaseVariablesKeys = Object.keys(releaseVariables);
  const basicVariablesMetaData = getBasicVariablesMetaData();
  const functionalVariables: CustomCssVariables[] = basicVariablesMetaData.filter(
    (variable) => releaseVariablesKeys.includes(variable.key) && allSDKEnabledVars[variable.key]
  );

  return processVariablesIntoGroups(functionalVariables, component);
};

/**
 * Gets all variables (including feature flag-dependent ones) grouped and sorted.
 * This function is async and waits for authentication and feature flag data.
 * Used to provide the complete set of variables after initial loading.
 *
 * @param releaseVariables - Object containing available variable keys
 * @param component - Component type to determine sort order
 * @returns Promise that resolves to array of grouped variables
 */
const getGroupedVariables = async (
  releaseVariables: any,
  component: VariableComponent = VariableComponent.Default
) => {
  const releaseVariablesKeys = Object.keys(releaseVariables);
  const variablesMetaDataObject = await variablesMetaData();
  const functionalVariables: CustomCssVariables[] = variablesMetaDataObject.filter(
    (variable) => releaseVariablesKeys.includes(variable.key) && allSDKEnabledVars[variable.key]
  );

  return processVariablesIntoGroups(functionalVariables, component);
};

export { getGroupedVariables, sortOrders, getSortOrder };
